import { parsePackInfo, type PackInfoSource, type ParsedPackInfo } from './ratePerBaseUnit';

// Short form for a product's packagingType, used wherever that value becomes
// the printed "Unit" on a document line (a quotation, a pipeline record's
// product line, a Tax Invoice, a Delivery Challan — this one function feeds
// all of them, so fixing it here fixes every document at once).
//
// These are the statutory GST UQC (Unit Quantity Code) values from the CBIC
// master list, not invented shorthand — a printed document showing "Bbl"/"Dr"
// for the exact same drum a different screen calls "DRUM" is a real GST
// filing inconsistency, not just a display nit. Anything not in this map (a
// free-typed custom unit, an unrecognised packagingType) passes through
// unchanged.
const PACKAGING_ABBREVIATIONS: Record<string, string> = {
  Box: 'BOX',
  // RBM's operational/document vocabulary. These codes are deliberately
  // explicit: dispatch calls a 7.5–26 LTR container BKT and a 210 LTR
  // container BRL, so documents must not turn them into a generic "PAC" or
  // "DRM" halfway through the sales chain.
  Bucket: 'BKT',
  Can: 'CAN',
  Barrel: 'BRL',
  Spray: 'OTH',
  Bottle: 'BTL',
  Pouch: 'PAC',
  Other: 'OTH',
};

export function abbreviatePackaging(packagingType?: string | null): string {
  const key = String(packagingType || '').trim();
  return PACKAGING_ABBREVIATIONS[key] || key;
}

/**
 * RBM's entire packaging vocabulary is exactly these four — confirmed
 * explicitly: "Bottle"/"Spray"/"Pouch"/"Other" are NOT real categories here,
 * however often the catalogue itself says one of them (17 products are
 * tagged 'Bottle', 2 'Spray', 15 'Pouch', 314 'Other'). Any packagingType
 * outside this set is always replaced by the volume-derived one below —
 * never shown as-is, unlike an earlier version of this rule that only
 * overrode the generic 'Other' and let 'Bottle' etc. straight through.
 */
const REAL_PACKAGING_TYPES = new Set(['Box', 'Bucket', 'Can', 'Barrel']);

/**
 * RBM's own packaging convention, purely as a function of a SINGLE
 * container's own volume/weight in LTR or KG: up to 5 → Box, 7.5–26 →
 * Bucket, 50/55 → Can, ~210 → Barrel (the boundaries below split the gaps
 * between those anchors). This is the size of ONE bottle/container, not a
 * case total — a case of 6 x 1 LTR bottles is still six 1-litre Boxes, not
 * one 6-litre Bucket, however many go in the case. Only meant to stand in
 * for a catalogue record whose own packagingType isn't one of the four real
 * categories — see derivePackagingType.
 */
export function packagingTypeFromQuantity(containerSize: number): string | null {
  if (!(containerSize > 0)) return null;
  if (containerSize <= 5) return 'Box';
  if (containerSize <= 30) return 'Bucket';
  if (containerSize <= 70) return 'Can';
  if (containerSize <= 250) return 'Barrel';
  return null;
}

/** A single pack's own size (unitSize), converted to its base unit (LTR/KG) — never the case/total quantity. */
function containerSizeInBaseUnit(info: ParsedPackInfo): number {
  if (info.unit === 'ML' || info.unit === 'G') return info.unitSize / 1000;
  return info.unitSize;
}

/**
 * The packaging container for a bare pack-size string ("210 LTR", "18 KG"),
 * or null when it doesn't parse to a real size at all (blank, "25NOS" with
 * no unit). Unlike derivePackagingType below, this NEVER falls back to
 * 'Box' for an unparseable size — used to auto-correct a line's Unit cell
 * as the rep types a Size, where "leave it alone, nothing to derive from
 * yet" is the right answer for an empty/unrecognised value, not a guess.
 */
export function containerPackagingType(packSize?: string | null): string | null {
  const info = parsePackInfo({ packSize });
  if (!info) return null;
  return packagingTypeFromQuantity(containerSizeInBaseUnit(info));
}

/**
 * The packaging type to treat as a product's real container: the catalogue's
 * own packagingType ONLY when it's already one of the four real categories
 * (Box/Bucket/Can/Barrel) — anything else (Other/Bottle/Spray/Pouch/blank)
 * is replaced by the one derived from the SIZE OF ONE PACK (not the case
 * total — see packagingTypeFromQuantity), so a product tagged 'Bottle' at
 * 900 ML correctly shows as Box, not "Btl".
 */
export function derivePackagingType(
  product?: (PackInfoSource & { packagingType?: string | null }) | null,
): string | undefined {
  const stored = String(product?.packagingType || '').trim();
  if (REAL_PACKAGING_TYPES.has(stored)) return stored;
  const info = parsePackInfo(product);
  const derived = info ? packagingTypeFromQuantity(containerSizeInBaseUnit(info)) : null;
  // No pack size to derive from at all (a bare, unpackaged catalogue row) —
  // 'Box' is the smallest/most common container rather than surfacing a
  // non-standard stored value ('Bottle'/'Spray'/'Pouch'/'Other'/blank).
  return derived || 'Box';
}
